import React from "react";
import { ViewKey } from "../../v2/components/ViewKey.jsx";
import { SEAT_STYLE, INK, statusPillStyle } from "./capacityStyles.js";
import { LONG_RANGE_NOTE } from "./capacityModel.js";
import { GRID_CELL, FORECAST_FADE_LABEL } from "./capacityGridStyles.js";
import { HowWeDefineThis } from "../../components/SpacePolicyDefinitions.jsx";
import { DEFAULT_BUFFER } from "../../services/seatFit.js";
export const outlookBands = (bufferPct = DEFAULT_BUFFER) => [
  { status: "comfortable", label: "Room", description: `below ${100 - bufferPct}% vs usable seats (attendance-adjusted)` },
  { status: "tight", label: "Near limit", description: `${100 - bufferPct}% through 100% vs usable seats (attendance-adjusted)` },
  { status: "over", label: "Over limit", description: "above 100% vs usable seats (attendance-adjusted)" },
];

const cell = (k) => ({ kind: "cell", color: GRID_CELL[k].bg, border: ["no_data", "history"].includes(k) ? GRID_CELL[k].border : "transparent", borderStyle: GRID_CELL[k].dashed ? "dashed" : "solid" });
const seat = (k) => {
  const [, borderStyle, ...color] = SEAT_STYLE[k].border.split(" ");
  return { kind: "cell", color: SEAT_STYLE[k].background, border: color.join(" "), borderStyle };
};

// The single Capacity key configuration. Exported for tests.
export function capacityKeyConfig({ view = "monthly", bufferPct = DEFAULT_BUFFER } = {}) {
  return {
    groups: [
      { label: "Status", columns: 2, items: [
        ...outlookBands(bufferPct).map(b => ({ label: b.label, labelStyle: statusPillStyle(b.status), description: b.description, swatch: cell(b.status) })),
        { label: "No data", description: "no demand, usable seats or attendance (empty square)", swatch: cell("no_data") },
        { label: "Under 3 months history", description: "trend not yet reliable", swatch: cell("history") },
      ] },
      { label: "Markers", items: [
        { label: "Committed change", description: "capacity schedule row that changes installed seats", swatch: { kind: "diamond", color: INK } },
        { label: "Changed by scenario", description: "1.5px outline where scenario status differs from today", swatch: { kind: "ring", color: INK, borderWidth: 1.5 } },
      ] },
      { label: "Seat grid", columns: 2, items: [
        { label: "Used", swatch: seat("used") },
        { label: "Spare", swatch: seat("spare") },
        { label: "Within max, used", swatch: seat("densUsed") },
        { label: "Within max, room", swatch: seat("densRoom") },
        { label: "Beyond max", description: "1 square = 1 usable seat; demand above usable seats and any valid adjusted maximum", swatch: seat("over") },
         { label: "Installed seats", description: "2px solid divider at installed seats, including inside a scaled square. If outside the represented range, disclosed in the summary.", swatch: { kind: "ring", color: INK, borderWidth: 2 } },
      ] },
    ],
    footer: `${FORECAST_FADE_LABEL}. This month uses assigned today; forecast demand starts next month. Seats = usable seats: installed seats adjusted for peak attendance. Maximum = recorded maximum × usable ÷ installed, same month; recorded headroom is disclosure, not a separate status. ` + (view === "quarterly"
      ? `The same next 36 months, grouped into calendar quarters (12 or 13). Partial endpoint quarters include only months in the window, shown at their fullest month (worst status, then highest ratio). Includes committed changes. ${LONG_RANGE_NOTE} Faded squares (same colours, reduced opacity) are long-range.`
      : `Each square is one market, one month across the next 36 months: assigned today this month, forecast from next month, against usable seats. Includes committed changes. ${LONG_RANGE_NOTE} Months 25–36 are faded. Hover or focus a square for the numbers.`),
  };
}

export function CapacityViewKey({ view, bufferPct }) {
  const cfg = capacityKeyConfig({ view, bufferPct });
  return <ViewKey testId="capacity-key" title="How to read Capacity" groups={cfg.groups} footer={<><p>{cfg.footer}</p><HowWeDefineThis /></>} />;
}
